import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Spin, Statistic } from "antd";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactECharts from "@/components/EChartsReact";
import message from "@/utils/globalMessage";
import { api } from "@redux/api";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import {
  CONTRACT_STATUS_LABELS,
  getDisplayStatus,
  normalizeContractResponse,
} from "@/containers/contract/constants";

const CHART_COLORS = ["#396598", "#52a9d6", "#7ed29b", "#f6c85f", "#ef8a62", "#9b8bd6"];

// Rows with no value for the grouped field still deserve a slice, but it must
// not be clickable — there is no filter value to navigate with.
const UNKNOWN_KEY = "__unknown__";

const groupRows = (rows, resolveKey, resolveLabel) => {
  const counts = rows.reduce((accumulator, row) => {
    const rawKey = resolveKey(row);
    const key =
      rawKey === undefined || rawKey === null || rawKey === ""
        ? UNKNOWN_KEY
        : String(rawKey);
    accumulator[key] = (accumulator[key] || 0) + 1;
    return accumulator;
  }, {});

  return Object.entries(counts).map(([key, value]) => ({
    key,
    name: key === UNKNOWN_KEY ? "Unassigned" : resolveLabel ? resolveLabel(key) : key,
    value,
    filterable: key !== UNKNOWN_KEY,
  }));
};

const buildPieOption = (title, data) => ({
  title: {
    text: title,
    left: "center",
    top: 8,
    textStyle: { color: "#396598", fontSize: 14 },
  },
  tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
  series: [
    {
      type: "pie",
      radius: ["38%", "65%"],
      center: ["50%", "58%"],
      data: data.map((item, index) => ({
        ...item,
        itemStyle: { color: CHART_COLORS[index % CHART_COLORS.length] },
      })),
    },
  ],
});

export default function FacilityContractDashboard() {
  const { companyId: routeCompanyId, facilityId: routeFacilityId } = useParams();
  const navigate = useNavigate();
  const selectedFacilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const companyId = routeCompanyId || facilityContext.companyId;
  // Route params are always strings; the contract API filters on an id list.
  const facilityId = Number(routeFacilityId) || selectedFacilityId;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!facilityId) {
      setRows([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    // Facility side reads /api/v2/contracts/ (FacilityContractViewSet). The
    // /api/v2/contract/contract-requests/ endpoint belongs to the contractor
    // portal and is authenticated with the contract-company session, so a
    // facility user can never load it.
    api
      .getContracts([facilityId], { page: 1, page_size: 1000 })
      .then((response) => {
        if (!active) return;
        setRows(normalizeContractResponse(response).rows);
      })
      .catch(() => {
        if (!active) return;
        setRows([]);
        message.error("Failed to load contract requests");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [facilityId]);

  const statusData = useMemo(
    () =>
      groupRows(
        rows,
        (row) => getDisplayStatus(row),
        (key) => CONTRACT_STATUS_LABELS[key] || key
      ),
    [rows]
  );
  const priorityData = useMemo(() => groupRows(rows, (row) => row.priority), [rows]);

  const awardedCount = useMemo(
    () => rows.filter((row) => row.assignTo != null || row.awarded_quote_id != null).length,
    [rows]
  );
  const awardedAmount = useMemo(
    () => rows.reduce((total, row) => total + (Number(row.awarded_amount) || 0), 0),
    [rows]
  );
  const contractorCount = useMemo(
    () => new Set(rows.map((row) => row.assignTo).filter(Boolean)).size,
    [rows]
  );

  // A chart slice drills into the contract request list, pre-filtered by the
  // value it represents.
  const openFilteredList = useCallback(
    (filterKey, value) => {
      const target = buildFacilityPath("contract-request", {
        companyId,
        facilityId,
        query: { [filterKey]: value },
      });
      navigate(target || "/switch-view");
    },
    [companyId, facilityId, navigate]
  );

  if (loading) {
    return <Spin style={{ margin: 40 }} />;
  }

  const summary = [
    { title: "Contract Requests", value: rows.length },
    { title: "Awarded", value: awardedCount },
    { title: "Awarded Amount", value: `$${awardedAmount.toLocaleString()}` },
    { title: "Contractors", value: contractorCount },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[12, 12]}>
        {summary.map(({ title, value }) => (
          <Col xs={24} sm={12} lg={6} key={title}>
            <Card size="small" style={{ height: 112, borderLeft: "3px solid #396598" }}>
              <Statistic title={title} value={value} />
            </Card>
          </Col>
        ))}
      </Row>
      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
        <Col xs={24} lg={12}>
          <Card bodyStyle={{ padding: 4 }}>
            <ReactECharts
              option={buildPieOption("Status", statusData)}
              onEvents={{
                click: ({ data }) => data?.filterable && openFilteredList("status", data.key),
              }}
              style={{ height: 300 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bodyStyle={{ padding: 4 }}>
            <ReactECharts
              option={buildPieOption("Priority", priorityData)}
              onEvents={{
                click: ({ data }) => data?.filterable && openFilteredList("priority", data.key),
              }}
              style={{ height: 300 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
